import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { useToast } from "@/hooks/use-toast";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Plus, Edit, Trash2, Building2, Store, Package, Users, Box } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function KitchenOutletManagement() {
  const { toast } = useToast();
  const [kitchens, setKitchens] = useState([]);
  const [outlets, setOutlets] = useState([]);
  const [opsManagers, setOpsManagers] = useState([]);
  const [products, setProducts] = useState([]);
  const [kitchenInventory, setKitchenInventory] = useState([]);
  const [selectedKitchen, setSelectedKitchen] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [kitchenDialogOpen, setKitchenDialogOpen] = useState(false);
  const [outletDialogOpen, setOutletDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [inventoryDialogOpen, setInventoryDialogOpen] = useState(false);
  
  // Form states
  const [kitchenForm, setKitchenForm] = useState({ name: "", address: "", phone: "", manager_name: "" });
  const [outletForm, setOutletForm] = useState({ name: "", kitchen_id: "", address: "", phone: "", contact_person: "" });
  const [inventoryForm, setInventoryForm] = useState({ product_id: "", quantity: 1, unit: "pcs" });
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedKitchen) {
      fetchOutlets(selectedKitchen);
      fetchKitchenInventory(selectedKitchen);
    }
  }, [selectedKitchen]);

  const fetchData = async () => {
    try {
      const [kitchensRes, opsRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/api/ops-extended/kitchens`, { credentials: "include" }),
        fetch(`${API_URL}/api/admin/ops-managers`, { credentials: "include" }),
        fetch(`${API_URL}/api/admin/products`, { credentials: "include" })
      ]);
      
      if (kitchensRes.ok) {
        const kitchensData = await kitchensRes.json();
        setKitchens(kitchensData);
        if (kitchensData.length > 0 && !selectedKitchen) {
          setSelectedKitchen(kitchensData[0].kitchen_id);
        }
      }
      
      if (opsRes.ok) {
        setOpsManagers(await opsRes.json());
      }
      
      if (productsRes.ok) {
        setProducts(await productsRes.json());
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOutlets = async (kitchenId) => {
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/outlets?kitchen_id=${kitchenId}`, { credentials: "include" });
      if (res.ok) setOutlets(await res.json());
    } catch (error) {
      console.error("Error fetching outlets:", error);
    }
  };

  const fetchKitchenInventory = async (kitchenId) => {
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/kitchen-inventory/${kitchenId}`, { credentials: "include" });
      if (res.ok) setKitchenInventory(await res.json());
    } catch (error) {
      console.error("Error fetching inventory:", error);
    }
  };

  // Kitchen CRUD
  const handleSaveKitchen = async () => {
    try {
      const url = editingId 
        ? `${API_URL}/api/ops-extended/kitchens/${editingId}`
        : `${API_URL}/api/ops-extended/kitchens`;
      
      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(kitchenForm)
      });
      
      if (res.ok) {
        toast({ title: `Kitchen ${editingId ? "updated" : "created"} successfully` });
        setKitchenDialogOpen(false);
        setKitchenForm({ name: "", address: "", phone: "", manager_name: "" });
        setEditingId(null);
        fetchData();
      } else {
        const error = await res.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save kitchen", variant: "destructive" });
    }
  };

  const handleDeleteKitchen = async (kitchenId) => {
    if (!window.confirm("Are you sure you want to delete this kitchen?")) return;
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/kitchens/${kitchenId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        toast({ title: "Kitchen deleted successfully" });
        fetchData();
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete kitchen", variant: "destructive" });
    }
  };

  // Outlet CRUD
  const handleSaveOutlet = async () => {
    try {
      const url = editingId 
        ? `${API_URL}/api/ops-extended/outlets/${editingId}`
        : `${API_URL}/api/ops-extended/outlets`;
      
      const res = await fetch(url, {
        method: editingId ? "PUT" : "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ ...outletForm, kitchen_id: selectedKitchen })
      });
      
      if (res.ok) {
        toast({ title: `Outlet ${editingId ? "updated" : "created"} successfully` });
        setOutletDialogOpen(false);
        setOutletForm({ name: "", kitchen_id: "", address: "", phone: "", contact_person: "" });
        setEditingId(null);
        fetchOutlets(selectedKitchen);
      } else {
        const error = await res.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to save outlet", variant: "destructive" });
    }
  };

  const handleDeleteOutlet = async (outletId) => {
    if (!window.confirm("Are you sure you want to delete this outlet?")) return;
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/outlets/${outletId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        toast({ title: "Outlet deleted successfully" });
        fetchOutlets(selectedKitchen);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete outlet", variant: "destructive" });
    }
  };

  // Kitchen Inventory CRUD
  const handleAddInventory = async () => {
    if (!inventoryForm.product_id) {
      toast({ title: "Error", description: "Please select a product", variant: "destructive" });
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/kitchen-inventory`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          kitchen_id: selectedKitchen,
          product_id: inventoryForm.product_id,
          quantity: parseFloat(inventoryForm.quantity) || 1,
          unit: inventoryForm.unit
        })
      });
      
      if (res.ok) {
        toast({ title: "Inventory item added successfully" });
        setInventoryDialogOpen(false);
        setInventoryForm({ product_id: "", quantity: 1, unit: "pcs" });
        fetchKitchenInventory(selectedKitchen);
      } else {
        const error = await res.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to add inventory", variant: "destructive" });
    }
  };

  const handleUpdateInventoryQty = async (inventoryId, newQty) => {
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/kitchen-inventory/${inventoryId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ quantity: parseFloat(newQty) })
      });
      if (res.ok) {
        toast({ title: "Inventory updated" });
        fetchKitchenInventory(selectedKitchen);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to update inventory", variant: "destructive" });
    }
  };

  const handleDeleteInventory = async (inventoryId) => {
    if (!window.confirm("Remove this item from kitchen inventory?")) return;
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/kitchen-inventory/${inventoryId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        toast({ title: "Inventory item removed" });
        fetchKitchenInventory(selectedKitchen);
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to remove inventory", variant: "destructive" });
    }
  };

  // Assign Ops Manager
  const handleAssignOpsManager = async (opsManagerId) => {
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/assign-kitchen/${opsManagerId}?kitchen_id=${selectedKitchen}`, {
        method: "PUT",
        credentials: "include"
      });
      if (res.ok) {
        toast({ title: "Ops manager assigned successfully" });
        setAssignDialogOpen(false);
        // Refresh ops managers list to reflect the new assignment
        const opsRes = await fetch(`${API_URL}/api/admin/ops-managers`, { credentials: "include" });
        if (opsRes.ok) {
          const updatedOps = await opsRes.json();
          setOpsManagers(updatedOps);
        }
      } else {
        const error = await res.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to assign ops manager", variant: "destructive" });
    }
  };

  // Unassign Ops Manager from Kitchen
  const handleUnassignOpsManager = async (opsManagerId) => {
    if (!window.confirm("Are you sure you want to remove this ops manager from the kitchen?")) return;
    try {
      const res = await fetch(`${API_URL}/api/ops-extended/unassign-kitchen/${opsManagerId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        toast({ title: "Ops manager removed from kitchen" });
        // Refresh ops managers list
        const opsRes = await fetch(`${API_URL}/api/admin/ops-managers`, { credentials: "include" });
        if (opsRes.ok) {
          const updatedOps = await opsRes.json();
          setOpsManagers(updatedOps);
        }
      } else {
        const error = await res.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to unassign ops manager", variant: "destructive" });
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="kitchen-outlet-management">
      {/* Kitchens Section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            Kitchens
          </CardTitle>
          <Dialog open={kitchenDialogOpen} onOpenChange={setKitchenDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingId(null); setKitchenForm({ name: "", address: "", phone: "", manager_name: "" }); }}>
                <Plus className="h-4 w-4 mr-2" /> Add Kitchen
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit Kitchen" : "Add New Kitchen"}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Name</Label>
                  <Input value={kitchenForm.name} onChange={(e) => setKitchenForm({ ...kitchenForm, name: e.target.value })} />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input value={kitchenForm.address} onChange={(e) => setKitchenForm({ ...kitchenForm, address: e.target.value })} />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={kitchenForm.phone} onChange={(e) => setKitchenForm({ ...kitchenForm, phone: e.target.value })} />
                </div>
                <div>
                  <Label>Manager Name</Label>
                  <Input value={kitchenForm.manager_name} onChange={(e) => setKitchenForm({ ...kitchenForm, manager_name: e.target.value })} />
                </div>
                <Button onClick={handleSaveKitchen} className="w-full">Save Kitchen</Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2 mb-4">
            {kitchens.map((kitchen) => (
              <Button
                key={kitchen.kitchen_id}
                variant={selectedKitchen === kitchen.kitchen_id ? "default" : "outline"}
                onClick={() => setSelectedKitchen(kitchen.kitchen_id)}
                className="flex items-center gap-2"
              >
                <Building2 className="h-4 w-4" />
                {kitchen.name}
              </Button>
            ))}
          </div>
          
          {selectedKitchen && (
            <div className="border rounded-lg p-4 bg-muted/50">
              {kitchens.filter(k => k.kitchen_id === selectedKitchen).map(kitchen => (
                <div key={kitchen.kitchen_id} className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold">{kitchen.name}</h3>
                    <p className="text-sm text-muted-foreground">{kitchen.address}</p>
                    {kitchen.phone && <p className="text-sm">Phone: {kitchen.phone}</p>}
                    {kitchen.manager_name && <p className="text-sm">Manager: {kitchen.manager_name}</p>}
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={() => {
                      setKitchenForm(kitchen);
                      setEditingId(kitchen.kitchen_id);
                      setKitchenDialogOpen(true);
                    }}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button variant="destructive" size="sm" onClick={() => handleDeleteKitchen(kitchen.kitchen_id)}>
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Assign Ops Manager */}
      {selectedKitchen && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Assigned Ops Managers
            </CardTitle>
            <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="assign-ops-manager-btn">
                  <Plus className="h-4 w-4 mr-2" /> Assign Ops Manager
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Assign Ops Manager to Kitchen</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {opsManagers.map((ops) => {
                    const isAssignedHere = ops.assigned_kitchen_id === selectedKitchen;
                    const assignedKitchen = ops.assigned_kitchen_id 
                      ? kitchens.find(k => k.kitchen_id === ops.assigned_kitchen_id)?.name 
                      : null;
                    return (
                      <div key={ops.user_id} className={`flex items-center justify-between p-3 border rounded-lg ${isAssignedHere ? 'bg-green-50 border-green-200' : ''}`}>
                        <div>
                          <p className="font-medium">{ops.name}</p>
                          <p className="text-sm text-muted-foreground">{ops.email}</p>
                          {assignedKitchen && (
                            <p className={`text-xs ${isAssignedHere ? 'text-green-600 font-medium' : 'text-orange-500'}`}>
                              {isAssignedHere ? '✓ Assigned to this kitchen' : `Currently: ${assignedKitchen}`}
                            </p>
                          )}
                        </div>
                        <Button 
                          size="sm" 
                          variant={isAssignedHere ? "secondary" : "default"}
                          onClick={() => handleAssignOpsManager(ops.user_id)}
                          disabled={isAssignedHere}
                          data-testid={`assign-btn-${ops.user_id}`}
                        >
                          {isAssignedHere ? 'Assigned' : 'Assign'}
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <div className="space-y-2" data-testid="assigned-ops-list">
              {opsManagers.filter(ops => ops.assigned_kitchen_id === selectedKitchen).length === 0 ? (
                <p className="text-muted-foreground">No ops managers assigned to this kitchen</p>
              ) : (
                opsManagers.filter(ops => ops.assigned_kitchen_id === selectedKitchen).map(ops => (
                  <div key={ops.user_id} className="flex items-center justify-between p-3 border rounded-lg bg-muted/30" data-testid={`assigned-ops-${ops.user_id}`}>
                    <div>
                      <p className="font-medium">{ops.name}</p>
                      <p className="text-sm text-muted-foreground">{ops.email}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-green-600 text-sm font-medium">✓ Assigned</span>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleUnassignOpsManager(ops.user_id)}
                        data-testid={`unassign-btn-${ops.user_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Outlets Section */}
      {selectedKitchen && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Store className="h-5 w-5" />
              Outlets
            </CardTitle>
            <Dialog open={outletDialogOpen} onOpenChange={setOutletDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => { setEditingId(null); setOutletForm({ name: "", kitchen_id: "", address: "", phone: "", contact_person: "" }); }}>
                  <Plus className="h-4 w-4 mr-2" /> Add Outlet
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{editingId ? "Edit Outlet" : "Add New Outlet"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Name</Label>
                    <Input value={outletForm.name} onChange={(e) => setOutletForm({ ...outletForm, name: e.target.value })} />
                  </div>
                  <div>
                    <Label>Address</Label>
                    <Input value={outletForm.address} onChange={(e) => setOutletForm({ ...outletForm, address: e.target.value })} />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input value={outletForm.phone} onChange={(e) => setOutletForm({ ...outletForm, phone: e.target.value })} />
                  </div>
                  <div>
                    <Label>Contact Person</Label>
                    <Input value={outletForm.contact_person} onChange={(e) => setOutletForm({ ...outletForm, contact_person: e.target.value })} />
                  </div>
                  <Button onClick={handleSaveOutlet} className="w-full">Save Outlet</Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Address</TableHead>
                  <TableHead>Contact</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {outlets.map((outlet) => (
                  <TableRow key={outlet.outlet_id}>
                    <TableCell className="font-medium">{outlet.name}</TableCell>
                    <TableCell>{outlet.address}</TableCell>
                    <TableCell>
                      {outlet.contact_person && <div>{outlet.contact_person}</div>}
                      {outlet.phone && <div className="text-sm text-muted-foreground">{outlet.phone}</div>}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => {
                        setOutletForm(outlet);
                        setEditingId(outlet.outlet_id);
                        setOutletDialogOpen(true);
                      }}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteOutlet(outlet.outlet_id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {outlets.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground">
                      No outlets found. Add your first outlet.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Kitchen Inventory Section */}
      {selectedKitchen && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Box className="h-5 w-5" />
              Kitchen Inventory
            </CardTitle>
            <Dialog open={inventoryDialogOpen} onOpenChange={setInventoryDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm">
                  <Plus className="h-4 w-4 mr-2" /> Add from Catalog
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Inventory Item</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label>Product from Catalog</Label>
                    <Select value={inventoryForm.product_id} onValueChange={(v) => setInventoryForm({ ...inventoryForm, product_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Select product" />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((p) => (
                          <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Quantity</Label>
                      <Input 
                        type="number" 
                        min="0" 
                        step="0.1"
                        value={inventoryForm.quantity} 
                        onChange={(e) => setInventoryForm({ ...inventoryForm, quantity: e.target.value })} 
                      />
                    </div>
                    <div>
                      <Label>Unit</Label>
                      <Select value={inventoryForm.unit} onValueChange={(v) => setInventoryForm({ ...inventoryForm, unit: v })}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pcs">pcs</SelectItem>
                          <SelectItem value="kg">kg</SelectItem>
                          <SelectItem value="liters">liters</SelectItem>
                          <SelectItem value="boxes">boxes</SelectItem>
                          <SelectItem value="packets">packets</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <Button onClick={handleAddInventory} className="w-full">Add to Inventory</Button>
                </div>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {kitchenInventory.map((item) => (
                <div key={item.inventory_id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium">{item.product_name}</p>
                    <p className="text-sm text-muted-foreground">ID: {item.product_id}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Input 
                      type="number"
                      min="0"
                      step="0.1"
                      className="w-24"
                      value={item.quantity}
                      onChange={(e) => handleUpdateInventoryQty(item.inventory_id, e.target.value)}
                    />
                    <span className="text-sm w-16">{item.unit}</span>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteInventory(item.inventory_id)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </div>
              ))}
              {kitchenInventory.length === 0 && (
                <p className="text-center text-muted-foreground py-4">
                  No inventory items. Add items from the product catalog.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
